import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase, Listing } from '@/lib/supabase';
import { formatPrice } from '@/lib/utils';
// emailService eliminado
import { 
    MapPin, Calendar, Maximize, Shield, User, ArrowLeft, Share2, Heart, 
    Send, Loader2, CheckCircle2, Ruler, Box, DoorOpen, Lock, Info, Euro, Eye, Flame,
    Edit, Archive, Trash2, ArchiveRestore, Tag
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function ListingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const viewIncremented = useRef(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchListing() {
      if (!id) return;
      const { data, error } = await supabase
        .from('listings')
        .select('*, profiles(full_name, phone, avatar_url)')
        .eq('id', id)
        .single();

      if (!error && data) {
        setListing(data);
        
        if (!viewIncremented.current) {
            viewIncremented.current = true;
            supabase.rpc('increment_view_count', { listing_id: id });
        }
      }
      setLoading(false);
    }
    fetchListing();

    if (user) {
      setContactForm(prev => ({
        ...prev,
        name: user.user_metadata?.full_name || '',
        email: user.email || ''
      }));
    }
  }, [id, user]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listing) return;
    
    setSending(true);
    setError(null);

    try {
      const { error: dbError } = await supabase
        .from('messages')
        .insert({
          storage_id: listing.id,
          owner_id: listing.user_id,
          sender_id: user?.id || null, 
          sender_email: contactForm.email,
          sender_name: contactForm.name,
          sender_phone: contactForm.phone,
          message: contactForm.message
        });

      if (dbError) throw dbError;

      // La base de datos detectará el nuevo mensaje y enviará el email automáticamente.
      setSent(true);
      setContactForm({ ...contactForm, message: '' }); 
    } catch (err: any) {
      console.error(err);
      setError('Hubo un error al guardar el mensaje. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: 'rented' | 'sold' | 'approved') => {
    if (!listing) return;
    setActionLoading(true);
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: newStatus })
            .eq('id', listing.id);

        if (error) throw error;
        setListing({ ...listing, status: newStatus });
    } catch (error) {
        console.error('Error updating status:', error);
        alert('No se pudo actualizar el estado.');
    } finally {
        setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!listing || !confirm('¿Estás seguro de que quieres eliminar este anuncio permanentemente?')) return;
    setActionLoading(true);
    try {
        const { error } = await supabase
            .from('listings')
            .delete()
            .eq('id', listing.id);

        if (error) throw error;
        navigate('/profile');
    } catch (error) {
        console.error('Error deleting listing:', error);
        alert('No se pudo eliminar el anuncio.');
    } finally {
        setActionLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (!listing) return <div className="min-h-screen flex items-center justify-center">Anuncio no encontrado</div>;

  const isOwner = user && user.id === listing.user_id;
  const images = listing.images && listing.images.length > 0 
    ? listing.images 
    : ['https://images.unsplash.com/photo-1590247813693-5541d1c609fd?auto=format&fit=crop&q=80&w=1200'];

  const ownerName = (listing as any).profiles?.full_name || listing.contact_name || 'Usuario de EncuentraTrastero';
  const viewCount = listing.view_count || 0;
  const isHighDemand = viewCount > 50;

  const translateFeature = (key: string) => {
    const map: Record<string, string> = {
        'access_street': 'A pie de calle',
        'access_basement': 'En sótano',
        'access_elevator': 'Acceso con ascensor',
        'access_freight_elevator': 'Acceso con montacargas',
        'access_garage': 'Acceso desde garaje',
        'access_wide_corridor': 'Pasillo ancho',
        'access_metal_door': 'Puerta metálica',
        'access_private_door': 'Puerta privada',
        'feat_lock': 'Cerradura propia',
        'feat_security': 'Vigilancia',
        'feat_light': 'Iluminación',
        'feat_ventilation': 'Ventilación',
        'feat_power': 'Toma eléctrica',
        'feat_24h': 'Acceso 24h',
        'feat_individual': 'Individual',
        'feat_shared': 'Compartido',
        'use_personal': 'Uso personal',
        'use_bicycle': 'Bicicletas',
        'use_documents': 'Documentos',
        'use_stock': 'Stock negocio',
        'use_office': 'Material oficina',
        'use_moving': 'Mudanzas'
    };
    return map[key] || key;
  };

  const features = listing.features || {};
  const accessFeatures = Object.keys(features).filter(k => k.startsWith('access_') && features[k]);
  const generalFeatures = Object.keys(features).filter(k => k.startsWith('feat_') && features[k]);
  const usageFeatures = Object.keys(features).filter(k => k.startsWith('use_') && features[k]);

  return (
    <div className="bg-white min-h-screen pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
        <Link to="/search" className="flex items-center text-slate-500 hover:text-blue-600 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a resultados
        </Link>
        <div className="flex gap-2">
          <div className={`flex items-center gap-1 text-sm mr-4 ${isHighDemand ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
            {isHighDemand ? <Flame className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{viewCount} vistas</span>
          </div>
          <button className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors">
            <Share2 className="w-5 h-5" />
          </button>
          <button className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors">
            <Heart className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isOwner && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                            <User className="w-5 h-5" /> Gestionar mi anuncio
                        </h2>
                        <p className="text-blue-700 text-sm mt-1">
                            Estado actual: <span className="font-bold uppercase">{listing.status === 'approved' ? 'Publicado' : listing.status === 'pending' ? 'Pendiente' : listing.status}</span>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button 
                            onClick={() => navigate(`/publish?edit=${listing.id}`)}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 font-medium rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                        >
                            <Edit className="w-4 h-4" /> Editar
                        </button>
                        {(listing.status === 'approved' || listing.status === 'pending') && (
                            <button 
                                onClick={() => handleStatusChange(listing.operation_type === 'rent' ? 'rented' : 'sold')}
                                disabled={actionLoading}
                                className={`flex items-center gap-2 px-4 py-2 text-white font-medium rounded-lg transition-colors ${
                                    listing.operation_type === 'rent' 
                                        ? 'bg-slate-800 hover:bg-slate-900' 
                                        : 'bg-purple-900 hover:bg-purple-950'
                                }`}
                            >
                                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                    listing.operation_type === 'rent' ? <Archive className="w-4 h-4" /> : <Tag className="w-4 h-4" />
                                )}
                                Marcar como {listing.operation_type === 'rent' ? 'Alquilado' : 'Vendido'}
                            </button>
                        )}
                        {(listing.status === 'rented' || listing.status === 'sold') && (
                            <button 
                                onClick={() => handleStatusChange('approved')}
                                disabled={actionLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                            >
                                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveRestore className="w-4 h-4" />}
                                Reactivar Anuncio
                            </button>
                        )}
                        <button 
                            onClick={handleDelete}
                            disabled={actionLoading}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 font-medium rounded-lg border border-red-200 hover:bg-red-50 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" /> Borrar
                        </button>
                    </div>
                </div>
            </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
            <div className="space-y-4">
                <div className="aspect-video bg-slate-100 rounded-2xl overflow-hidden shadow-sm relative">
                <img 
                    src={images[activeImage]} 
                    alt={listing.title} 
                    className="w-full h-full object-cover"
                />
                {isHighDemand && (
                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg flex items-center gap-1 animate-pulse z-10">
                        <Flame className="w-3.5 h-3.5" /> ¡Muy solicitado!
                    </div>
                )}
                </div>
                {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, idx) => (
                    <button
                        key={idx}
                        onClick={() => setActiveImage(idx)}
                        className={`relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${activeImage === idx ? 'border-blue-600 ring-2 ring-blue-100' : 'border-transparent opacity-70 hover:opacity-100'}`}
                    >
                        <img src={img} alt={`Vista ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                    ))}
                </div>
                )}
            </div>

            <div>
                <div className="flex items-start justify-between mb-4">
                <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                            {listing.operation_type === 'rent' ? 'Alquiler' : 'Venta'}
                        </span>
                        {isHighDemand && (
                            <span className="inline-flex items-center px-3 py-1 bg-red-50 text-red-700 border border-red-100 rounded-full text-sm font-semibold">
                                <Flame className="w-3 h-3 mr-1" /> Tendencia
                            </span>
                        )}
                        {listing.status === 'rented' && (
                            <span className="inline-flex items-center px-3 py-1 bg-slate-800 text-white border border-slate-700 rounded-full text-sm font-semibold">
                                <Archive className="w-3 h-3 mr-1" /> ALQUILADO
                            </span>
                        )}
                        {listing.status === 'sold' && (
                            <span className="inline-flex items-center px-3 py-1 bg-purple-900 text-white border border-purple-700 rounded-full text-sm font-semibold">
                                <Tag className="w-3 h-3 mr-1" /> VENDIDO
                            </span>
                        )}
                    </div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">{listing.title}</h1>
                    <div className="flex items-center text-slate-500">
                    <MapPin className="w-5 h-5 mr-2 text-slate-400" />
                    {listing.address ? `${listing.address}, ` : ''}{listing.city}, {listing.province}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-3xl font-bold text-blue-600">{formatPrice(listing.price)}</div>
                    {listing.operation_type === 'rent' && <div className="text-slate-500">/mes</div>}
                    {listing.expenses_included && <div className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded mt-1">Gastos incluidos</div>}
                </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-slate-100">
                <div className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-xl">
                    <Maximize className="w-6 h-6 text-blue-500 mb-2" />
                    <span className="text-sm text-slate-500">Superficie</span>
                    <span className="font-bold text-slate-900">{listing.area_m2} m²</span>
                </div>
                {listing.height_m && (
                    <div className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-xl">
                        <Ruler className="w-6 h-6 text-indigo-500 mb-2" />
                        <span className="text-sm text-slate-500">Altura</span>
                        <span className="font-bold text-slate-900">{listing.height_m} m</span>
                    </div>
                )}
                {listing.volume_m3 && (
                    <div className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-xl">
                        <Box className="w-6 h-6 text-amber-500 mb-2" />
                        <span className="text-sm text-slate-500">Volumen</span>
                        <span className="font-bold text-slate-900">{listing.volume_m3} m³</span>
                    </div>
                )}
                <div className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-xl">
                    <Shield className="w-6 h-6 text-green-500 mb-2" />
                    <span className="text-sm text-slate-500">Seguridad</span>
                    <span className="font-bold text-slate-900">Verificada</span>
                </div>
                </div>

                <div className="mt-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Descripción</h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {listing.description || 'Sin descripción detallada.'}
                </p>
                </div>

                <div className="mt-8 space-y-6">
                    {accessFeatures.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                <DoorOpen className="w-5 h-5 text-blue-600" /> Acceso
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {accessFeatures.map(key => (
                                    <span key={key} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-100">
                                        {translateFeature(key)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {generalFeatures.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                <Lock className="w-5 h-5 text-blue-600" /> Características
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {generalFeatures.map(key => (
                                    <span key={key} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium border border-slate-200">
                                        {translateFeature(key)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {usageFeatures.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                <Box className="w-5 h-5 text-blue-600" /> Usos Permitidos
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {usageFeatures.map(key => (
                                    <span key={key} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-100">
                                        {translateFeature(key)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="mt-8 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Info className="w-5 h-5 text-slate-500" /> Información Adicional
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex justify-between py-2 border-b border-slate-200">
                            <span className="text-slate-500">Disponibilidad</span>
                            <span className="font-medium text-slate-900">
                                {listing.available_from && new Date(listing.available_from) > new Date() 
                                    ? `A partir del ${new Date(listing.available_from).toLocaleDateString()}` 
                                    : 'Inmediata'}
                            </span>
                        </div>
                        {listing.min_months && (
                            <div className="flex justify-between py-2 border-b border-slate-200">
                                <span className="text-slate-500">Estancia mínima</span>
                                <span className="font-medium text-slate-900">{listing.min_months} meses</span>
                            </div>
                        )}
                        {listing.community_fee ? (
                            <div className="flex justify-between py-2 border-b border-slate-200">
                                <span className="text-slate-500">Comunidad</span>
                                <span className="font-medium text-slate-900">{listing.community_fee} €/mes</span>
                            </div>
                        ) : null}
                        {listing.ibi ? (
                            <div className="flex justify-between py-2 border-b border-slate-200">
                                <span className="text-slate-500">IBI</span>
                                <span className="font-medium text-slate-900">{listing.ibi} €/año</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            </div>

            <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 sticky top-24">
                <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-slate-200 rounded-full flex items-center justify-center overflow-hidden">
                    <User className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                    <h3 className="font-bold text-slate-900">{ownerName}</h3>
                    <p className="text-sm text-slate-500">Propietario</p>
                </div>
                </div>

                {isHighDemand && (
                    <div className="bg-red-50 p-3 rounded-lg mb-6 border border-red-100 flex items-start gap-2 animate-in fade-in slide-in-from-bottom-2">
                        <Flame className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-red-800">¡Alta Demanda!</p>
                            <p className="text-xs text-red-700 mt-0.5">
                                Este trastero tiene muchas visitas. Contacta ahora antes de que se alquile.
                            </p>
                        </div>
                    </div>
                )}

                {isOwner ? (
                    <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-200">
                        <p className="text-slate-600 text-sm mb-2">Este es tu anuncio.</p>
                        <p className="text-xs text-slate-400">Usa el panel superior para editarlo.</p>
                    </div>
                ) : sent ? (
                <div className="bg-green-50 p-6 rounded-xl text-center border border-green-100 animate-in fade-in duration-300">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-bold text-green-800 mb-2">¡Mensaje Enviado!</h3>
                    <p className="text-sm text-green-700 mb-4">
                    El propietario ha recibido tu solicitud y te contactará pronto.
                    </p>
                    <button 
                    onClick={() => setSent(false)}
                    className="text-sm font-medium text-green-600 hover:text-green-800 underline"
                    >
                    Enviar otro mensaje
                    </button>
                </div>
                ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                    {!user && (
                        <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-700 mb-2">
                            Inicia sesión para autocompletar tus datos y gestionar tus mensajes.
                        </div>
                    )}
                    <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Tu Nombre</label>
                    <input 
                        type="text" 
                        required 
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        value={contactForm.name}
                        onChange={e => setContactForm({...contactForm, name: e.target.value})}
                    />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Tu Email</label>
                    <input 
                        type="email" 
                        required 
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        value={contactForm.email}
                        onChange={e => setContactForm({...contactForm, email: e.target.value})}
                    />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Tu Teléfono (Opcional)</label>
                    <input 
                        type="tel" 
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        value={contactForm.phone}
                        onChange={e => setContactForm({...contactForm, phone: e.target.value})}
                    />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Mensaje</label>
                    <textarea 
                        required 
                        rows={4}
                        placeholder="Hola, estoy interesado en este trastero..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                        value={contactForm.message}
                        onChange={e => setContactForm({...contactForm, message: e.target.value})}
                    />
                    </div>
                    {error && <p className="text-xs text-red-500">{error}</p>}
                    <button 
                    type="submit" 
                    disabled={sending}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    Contactar Propietario
                    </button>
                </form>
                )}
                <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-400">
                    Al contactar aceptas nuestros términos y condiciones. Nunca envíes dinero por adelantado sin ver el trastero.
                </p>
                </div>
            </div>
            </div>
        </div>
      </div>
    </div>
  );
}
